import React from 'react';
import SvgIcon from '@mui/material/SvgIcon';
import InsertDriveFileSharp from '@mui/icons-material/InsertDriveFileSharp';
import CodeSharp from '@mui/icons-material/CodeSharp';
import HttpSharp from '@mui/icons-material/HttpSharp';
import ListSharp from '@mui/icons-material/ListSharp';
import CallSplitSharp from '@mui/icons-material/CallSplitSharp';
import MergeTypeSharp from '@mui/icons-material/MergeTypeSharp';
import CloudUploadSharp from '@mui/icons-material/CloudUploadSharp';
import CloudDownloadSharp from '@mui/icons-material/CloudDownloadSharp';
import PlaylistAddSharp from '@mui/icons-material/PlaylistAddSharp';
import CreateNewFolderSharp from '@mui/icons-material/CreateNewFolderSharp';
import FolderZipSharp from '@mui/icons-material/FolderZipSharp';
import StorageSharp from '@mui/icons-material/StorageSharp';
import LockSharp from '@mui/icons-material/LockSharp';
import MenuBookSharp from '@mui/icons-material/MenuBookSharp';
import FormatQuoteSharp from '@mui/icons-material/FormatQuoteSharp';
import DataObjectSharp from '@mui/icons-material/DataObjectSharp';
import MailSharp from '@mui/icons-material/MailSharp';
import GetAppSharp from '@mui/icons-material/GetAppSharp';
import TagSharp from '@mui/icons-material/TagSharp';
import FolderOpenSharp from '@mui/icons-material/FolderOpenSharp';
import LoopSharp from '@mui/icons-material/LoopSharp';
import BusinessSharp from '@mui/icons-material/BusinessSharp';
import MarkEmailReadSharp from '@mui/icons-material/MarkEmailReadSharp';
import CloudQueueSharp from '@mui/icons-material/CloudQueueSharp';
import VisibilitySharp from '@mui/icons-material/VisibilitySharp';
import BatchPredictionSharp from '@mui/icons-material/BatchPredictionSharp';
import SyncSharp from '@mui/icons-material/SyncSharp';
import SendSharp from '@mui/icons-material/SendSharp';
import ExtensionSharp from '@mui/icons-material/ExtensionSharp';
import AccessTimeSharp from '@mui/icons-material/AccessTimeSharp';
import Inventory2Sharp from '@mui/icons-material/Inventory2Sharp';

/** Category colors for step flow – scan by category at a glance */
export const STEP_FLOW_COLORS = {
	/** File operations: teal */
	FILE_OPS: '#00796B',
	/** Azure adapters: Azure blue */
	AZURE: '#0078D4',
	/** SAP BAPI: gold */
	SAP: '#F0B429',
	/** Mail: red */
	MAIL: '#D32F2F',
	/** REST/SOAP/HTTP API: blue */
	API: '#1976D2',
	/** Database: purple */
	DATABASE: '#7B1FA2',
	/** Security / encode (SHA512, BASE64): indigo */
	SECURITY: '#5C6BC0',
	/** Template / code (Freemarker, GraalVM): indigo */
	TEMPLATE: '#3949AB',
	/** Variable / data: deep purple */
	VARIABLE: '#5E35B1',
	/** Loop / control flow: purple */
	LOOP: '#AB47BC',
	/** ERP / business (Quickbook, Odoo): violet */
	ERP: '#6A1B9A',
	/** Default / other: grey */
	DEFAULT: '#616161',
} as const;

type AdapterConfig = { icon: React.ComponentType<React.ComponentProps<typeof SvgIcon>>; color: string };

const ADAPTER_CONFIG: Record<string, AdapterConfig> = {
	// File operations – teal
	FILE: { icon: InsertDriveFileSharp, color: STEP_FLOW_COLORS.FILE_OPS },
	BATCHFILE: { icon: ListSharp, color: STEP_FLOW_COLORS.FILE_OPS },
	FILESPLIT: { icon: CallSplitSharp, color: STEP_FLOW_COLORS.FILE_OPS },
	FILEMERGE: { icon: MergeTypeSharp, color: STEP_FLOW_COLORS.FILE_OPS },
	FILEUPLOAD: { icon: CloudUploadSharp, color: STEP_FLOW_COLORS.FILE_OPS },
	FILEDOWNLOAD: { icon: CloudDownloadSharp, color: STEP_FLOW_COLORS.FILE_OPS },
	FILEAPPEND: { icon: PlaylistAddSharp, color: STEP_FLOW_COLORS.FILE_OPS },
	FILECREATE: { icon: CreateNewFolderSharp, color: STEP_FLOW_COLORS.FILE_OPS },
	FILEZIP: { icon: FolderZipSharp, color: STEP_FLOW_COLORS.FILE_OPS },
	FILEUNZIP: { icon: FolderOpenSharp, color: STEP_FLOW_COLORS.FILE_OPS },
	// API / HTTP – blue
	SOAP: { icon: CodeSharp, color: STEP_FLOW_COLORS.API },
	REST: { icon: HttpSharp, color: STEP_FLOW_COLORS.API },
	HTTPDOWNLOAD: { icon: GetAppSharp, color: STEP_FLOW_COLORS.API },
	// Azure – Azure blue
	AZUREUPLOAD: { icon: CloudUploadSharp, color: STEP_FLOW_COLORS.AZURE },
	AZURECREATE: { icon: CloudQueueSharp, color: STEP_FLOW_COLORS.AZURE },
	AZUREREAD: { icon: VisibilitySharp, color: STEP_FLOW_COLORS.AZURE },
	AZUREMERGE: { icon: MergeTypeSharp, color: STEP_FLOW_COLORS.AZURE },
	AZURESPLIT: { icon: CallSplitSharp, color: STEP_FLOW_COLORS.AZURE },
	AZUREBATCH: { icon: BatchPredictionSharp, color: STEP_FLOW_COLORS.AZURE },
	AZUREDOWNLOAD: { icon: CloudDownloadSharp, color: STEP_FLOW_COLORS.AZURE },
	// SAP – gold
	SAPBAPIFETCH: { icon: SyncSharp, color: STEP_FLOW_COLORS.SAP },
	SAPBAPIPOST: { icon: SendSharp, color: STEP_FLOW_COLORS.SAP },
	// Database – purple
	DATABASE: { icon: StorageSharp, color: STEP_FLOW_COLORS.DATABASE },
	// Security / encode – indigo
	SHA512: { icon: LockSharp, color: STEP_FLOW_COLORS.SECURITY },
	BASE64: { icon: TagSharp, color: STEP_FLOW_COLORS.SECURITY },
	// Template / code – indigo
	FREEMARKER: { icon: FormatQuoteSharp, color: STEP_FLOW_COLORS.TEMPLATE },
	GRAALVM: { icon: Inventory2Sharp, color: STEP_FLOW_COLORS.TEMPLATE },
	// Variable / data – deep purple (global variable = clock/time)
	GLOBALVARIABLE: { icon: AccessTimeSharp, color: STEP_FLOW_COLORS.VARIABLE },
	DATARETRIEVE: { icon: GetAppSharp, color: STEP_FLOW_COLORS.VARIABLE },
	// Mail – red
	MAIL: { icon: MailSharp, color: STEP_FLOW_COLORS.MAIL },
	MAILDOWNLOAD: { icon: MarkEmailReadSharp, color: STEP_FLOW_COLORS.MAIL },
	// Loop – purple
	SUBLOOP: { icon: LoopSharp, color: STEP_FLOW_COLORS.LOOP },
	// ERP / business – violet
	QUICKBOOK: { icon: MenuBookSharp, color: STEP_FLOW_COLORS.ERP },
	ODOO: { icon: BusinessSharp, color: STEP_FLOW_COLORS.ERP },
};

const DEFAULT_CONFIG: AdapterConfig = { icon: ExtensionSharp, color: STEP_FLOW_COLORS.DEFAULT };

function getAdapterKey(adapter: string): string {
	return (adapter || '').trim().toUpperCase();
}

export function getStepFlowAdapterConfig(adapter: string): AdapterConfig {
	const key = getAdapterKey(adapter);
	return ADAPTER_CONFIG[key] ?? DEFAULT_CONFIG;
}

export function getStepFlowAdapterIcon(adapter: string): React.ComponentType<React.ComponentProps<typeof SvgIcon>> {
	return getStepFlowAdapterConfig(adapter).icon;
}

export function getStepFlowAdapterColor(adapter: string): string {
	return getStepFlowAdapterConfig(adapter).color;
}

interface StepFlowAdapterIconProps {
	adapter: string;
	sx?: object;
	fontSize?: 'small' | 'medium' | 'large' | 'inherit';
	/** If set, icon uses category color; otherwise inherits (e.g. from parent color) */
	useCategoryColor?: boolean;
}

export default function StepFlowAdapterIcon({
	adapter,
	sx,
	fontSize = 'large',
	useCategoryColor = true,
}: StepFlowAdapterIconProps) {
	const { icon: Icon, color } = getStepFlowAdapterConfig(adapter);
	const size = fontSize === 'large' ? 40 : fontSize === 'medium' ? 32 : 24;
	return (
		<Icon
			sx={{
				fontSize: size,
				...(useCategoryColor && { color }),
				...sx,
			}}
		/>
	);
}
